import type { Outfit } from "@mathbee/engine/shop";
import { useContext, useId } from "react";
import { useOutfitArt } from "./outfitArt.ts";
import { OutfitContext } from "./outfitContext.ts";
import styles from "./Bee.module.css";

export type BeeExpression = "happy" | "wave" | "cheer" | "think" | "oops" | "sleep";

interface Props {
  expression?: BeeExpression;
  size?: number;
  /** what this bee wears (default: the active child's outfit in child mode, SPEC 7.5) */
  outfit?: Outfit;
  animate?: boolean;
  className?: string;
  title?: string;
}

const INK = "#3d2b1f";

/** Ong MathBee, hand-drawn SVG (SPEC §9.4). Layers: wings · body · arms · head · face · outfit. */
export function Bee({
  expression = "happy",
  size = 120,
  outfit,
  animate = true,
  className,
  title,
}: Props) {
  const uid = useId().replace(/:/g, "");
  const id = (n: string) => `${n}-${uid}`;
  const childOutfit = useContext(OutfitContext);
  const worn: Outfit = outfit ?? childOutfit;
  const art = useOutfitArt(Object.keys(worn).length > 0);
  const paint = art?.wingPaint(worn.wings) ?? null;
  const part = (layer: "behind" | "back" | "backFront" | "neck" | "glasses" | "hat") =>
    art ? <art.OutfitPart outfit={worn} layer={layer} /> : null;
  const wingStroke = paint?.stroke ?? "#8fcdf0";
  const vein = paint?.vein ?? "#a9dbf6";
  const armsUp = expression === "cheer";
  const wave = expression === "wave";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 220"
      strokeLinejoin="round"
      strokeLinecap="round"
      width={size}
      height={(size * 220) / 200}
      className={`${styles.bee} ${animate ? styles.animate : ""} ${styles[expression] ?? ""} ${className ?? ""}`}
      role="img"
      aria-label={title ?? "Ong MathBee"}
    >
      <defs>
        <radialGradient id={id("body")} cx="40%" cy="30%" r="75%">
          <stop offset="0" stopColor="#ffe98f" />
          <stop offset="0.55" stopColor="#ffc727" />
          <stop offset="1" stopColor="#f29f00" />
        </radialGradient>
        <radialGradient id={id("head")} cx="38%" cy="30%" r="75%">
          <stop offset="0" stopColor="#fff0a8" />
          <stop offset="0.6" stopColor="#ffcf3a" />
          <stop offset="1" stopColor="#f4a800" />
        </radialGradient>
        <linearGradient id={id("wing")} x1="0" y1="0" x2="0" y2="1">
          {paint ? (
            paint.stops.map((c, i) => (
              <stop
                key={c}
                offset={i / (paint.stops.length - 1)}
                stopColor={c}
                stopOpacity="0.92"
              />
            ))
          ) : (
            <>
              <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
              <stop offset="1" stopColor="#bfe7ff" stopOpacity="0.85" />
            </>
          )}
        </linearGradient>
        <clipPath id={id("clip")}>
          <ellipse cx="100" cy="146" rx="47" ry="43" />
        </clipPath>
      </defs>

      <ellipse
        cx="100"
        cy="210"
        rx="44"
        ry="7"
        fill="rgb(31 42 68 / 0.12)"
        className={styles.shadow}
      />

      <g className={styles.float}>
        {part("behind")}
        {/* wings */}
        <g className={styles.wingLeft}>
          <ellipse
            cx="46"
            cy="104"
            rx="40"
            ry="26"
            transform="rotate(-32 46 104)"
            fill={`url(#${id("wing")})`}
            stroke={wingStroke}
            strokeWidth="3"
          />
          <path
            d="M26 96 Q48 102 66 116"
            stroke={vein}
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
          />
        </g>
        <g className={styles.wingRight}>
          <ellipse
            cx="154"
            cy="104"
            rx="40"
            ry="26"
            transform="rotate(32 154 104)"
            fill={`url(#${id("wing")})`}
            stroke={wingStroke}
            strokeWidth="3"
          />
          <path
            d="M174 96 Q152 102 134 116"
            stroke={vein}
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
          />
        </g>

        {part("back")}
        {/* body + stripes + stinger */}
        <path d="M92 186 Q100 202 108 186 Z" fill={INK} stroke={INK} strokeWidth="3" />
        <ellipse
          cx="100"
          cy="146"
          rx="47"
          ry="43"
          fill={`url(#${id("body")})`}
          stroke="#e09a00"
          strokeWidth="3"
        />
        <g clipPath={`url(#${id("clip")})`}>
          <rect x="40" y="134" width="120" height="13" rx="6" fill={INK} />
          <rect x="40" y="160" width="120" height="13" rx="6" fill={INK} />
        </g>
        <ellipse cx="82" cy="126" rx="14" ry="7" fill="#fff6c8" opacity="0.7" />

        {part("backFront")}
        {/* arms */}
        <path
          d={armsUp ? "M58 140 Q40 124 40 104" : "M58 146 Q42 146 36 134"}
          stroke={INK}
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
        />
        <circle
          cx={armsUp ? 40 : 35}
          cy={armsUp ? 101 : 131}
          r="7"
          fill="#ffd54a"
          stroke={INK}
          strokeWidth="3"
        />
        <g className={wave ? styles.waveArm : undefined}>
          <path
            d={armsUp || wave ? "M142 140 Q160 124 160 104" : "M142 146 Q158 146 164 134"}
            stroke={INK}
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
          />
          <circle
            cx={armsUp || wave ? 160 : 165}
            cy={armsUp || wave ? 101 : 131}
            r="7"
            fill="#ffd54a"
            stroke={INK}
            strokeWidth="3"
          />
        </g>

        {/* antennae */}
        <path
          d="M84 46 Q76 24 60 20"
          stroke={INK}
          strokeWidth="5"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M116 46 Q124 24 140 20"
          stroke={INK}
          strokeWidth="5"
          fill="none"
          strokeLinecap="round"
        />
        <circle cx="58" cy="19" r="7" fill={INK} />
        <circle cx="142" cy="19" r="7" fill={INK} />
        <circle cx="56" cy="17" r="2" fill="#fff" opacity="0.7" />
        <circle cx="140" cy="17" r="2" fill="#fff" opacity="0.7" />

        {/* head */}
        <circle
          cx="100"
          cy="86"
          r="47"
          fill={`url(#${id("head")})`}
          stroke="#e09a00"
          strokeWidth="3"
        />
        <ellipse
          cx="80"
          cy="60"
          rx="16"
          ry="9"
          fill="#fff8d0"
          opacity="0.75"
          transform="rotate(-20 80 60)"
        />

        <Face expression={expression} />

        {part("neck")}
        {part("glasses")}
        {part("hat")}
      </g>
    </svg>
  );
}

function Face({ expression }: { expression: BeeExpression }) {
  const pupil = expression === "think" ? { dx: -3, dy: -4 } : { dx: 2, dy: 2 };
  const closed = expression === "sleep";
  return (
    <g>
      {/* cheeks */}
      <ellipse cx="70" cy="102" rx="9" ry="5.5" fill="#ff9aa2" opacity="0.65" />
      <ellipse cx="130" cy="102" rx="9" ry="5.5" fill="#ff9aa2" opacity="0.65" />
      {/* eyes */}
      {closed ? (
        <>
          <path
            d="M70 86 Q81 94 92 86"
            stroke={INK}
            strokeWidth="4.5"
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M108 86 Q119 94 130 86"
            stroke={INK}
            strokeWidth="4.5"
            fill="none"
            strokeLinecap="round"
          />
        </>
      ) : (
        <g className={styles.eyes}>
          {[81, 119].map((cx) => (
            <g key={cx}>
              <ellipse cx={cx} cy="82" rx="12" ry="14" fill="#fff" />
              <circle cx={cx + pupil.dx} cy={82 + pupil.dy} r="8.5" fill="#2b1d14" />
              <circle cx={cx + pupil.dx + 3} cy={82 + pupil.dy - 3.5} r="3" fill="#fff" />
              <circle cx={cx + pupil.dx - 2.5} cy={82 + pupil.dy + 3} r="1.4" fill="#fff" />
            </g>
          ))}
        </g>
      )}
      {expression === "oops" && (
        <>
          <path
            d="M68 64 Q78 60 88 64"
            stroke={INK}
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
            transform="rotate(-12 78 62)"
          />
          <path
            d="M112 64 Q122 60 132 64"
            stroke={INK}
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
            transform="rotate(12 122 62)"
          />
        </>
      )}
      {/* mouth */}
      {expression === "cheer" || expression === "wave" ? (
        <g>
          <path
            d="M86 103 Q100 124 114 103 Z"
            fill="#7a2e1a"
            stroke={INK}
            strokeWidth="3"
            strokeLinejoin="round"
          />
          <ellipse cx="100" cy="113" rx="7" ry="4" fill="#ff8fa0" />
        </g>
      ) : expression === "oops" ? (
        <ellipse cx="100" cy="108" rx="6" ry="7" fill="#7a2e1a" stroke={INK} strokeWidth="2.5" />
      ) : expression === "think" ? (
        <path
          d="M92 108 Q100 104 108 108"
          stroke={INK}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
      ) : (
        <path
          d="M87 103 Q100 116 113 103"
          stroke={INK}
          strokeWidth="4.5"
          fill="none"
          strokeLinecap="round"
        />
      )}
    </g>
  );
}
